import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { api } from '@/lib/api';
import type { HistorieZmen, PagedResult } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Enumeracne hodnoty pro filtr "Entita". Drzime je v jednom poli, at se
// snadno doplnuji (musi sedet s tim, co backend ulozi do HistorieZmen.Entita).
const ENTITY_TYPES = [
  'Operativa',
  'Mechanizace',
  'Cinnost',
  'User',
  'UserRole',
  'Schvaleni',
  'Zastup',
  'PolozkaOperativy',
  'MaterialPolozky',
] as const;
type EntityType = (typeof ENTITY_TYPES)[number];

const ANY_ENTITY = '__any__';

// Hezky vystup JSON, kdyz to JSON je. Jinak vrati surovy retezec.
export function pretty(s: string | null | undefined): string {
  if (s == null) return '';
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

interface Filters {
  od: string;
  doDate: string;
  userId: string;
  entita: string; // '' or one of ENTITY_TYPES
  entitaId: string;
  page: number;
  pageSize: number;
}

const DEFAULT_FILTERS: Filters = {
  od: '',
  doDate: '',
  userId: '',
  entita: '',
  entitaId: '',
  page: 1,
  pageSize: 50,
};

export function AuditPage() {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <div className="flex items-start">
        <Card className="max-w-md">
          <CardHeader>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-md bg-destructive/10 text-destructive">
              <Lock className="h-5 w-5" />
            </div>
            <CardTitle>Nemáte oprávnění</CardTitle>
            <CardDescription>
              Pro zobrazení audit logu je vyžadována role <strong>ADMIN</strong>.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return <AuditView />;
}

function buildQueryString(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.od) params.set('od', filters.od);
  if (filters.doDate) params.set('doDate', filters.doDate);
  if (filters.userId.trim()) params.set('userId', filters.userId.trim());
  if (filters.entita) params.set('entita', filters.entita);
  if (filters.entitaId.trim()) params.set('entitaId', filters.entitaId.trim());
  params.set('page', String(filters.page));
  params.set('pageSize', String(filters.pageSize));
  return params.toString();
}

function AuditView() {
  // Filtr drzime v dvojici "draft" + "applied" — draft uzivatel ladi v UI,
  // applied je to, podle ceho se aktualne dotazuje. Diky tomu nemusi prebehnout
  // dotaz na kazdy stisk klavesy.
  const [draft, setDraft] = useState<Filters>(DEFAULT_FILTERS);
  const [applied, setApplied] = useState<Filters>(DEFAULT_FILTERS);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const qs = buildQueryString(applied);

  const listQuery = useQuery<PagedResult<HistorieZmen>>({
    queryKey: ['historie', qs],
    queryFn: () => api<PagedResult<HistorieZmen>>(`/historie?${qs}`),
  });

  const data = listQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    setApplied({ ...draft, page: 1 });
  };

  const handleReset = () => {
    setDraft(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
  };

  const handlePage = (delta: number) => {
    setApplied((a) => ({ ...a, page: Math.max(1, Math.min(totalPages, a.page + delta)) }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Audit log</h2>
        <p className="text-sm text-muted-foreground">Historie změn napříč aplikací</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtry</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="audit-od">Od</Label>
              <Input
                id="audit-od"
                type="date"
                value={draft.od}
                onChange={(e) => setDraft((d) => ({ ...d, od: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-do">Do</Label>
              <Input
                id="audit-do"
                type="date"
                value={draft.doDate}
                onChange={(e) => setDraft((d) => ({ ...d, doDate: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-entita">Entita</Label>
              <Select
                value={draft.entita === '' ? ANY_ENTITY : draft.entita}
                onValueChange={(v) =>
                  setDraft((d) => ({ ...d, entita: v === ANY_ENTITY ? '' : v }))
                }
              >
                <SelectTrigger id="audit-entita">
                  <SelectValue placeholder="Vše" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_ENTITY}>Vše</SelectItem>
                  {ENTITY_TYPES.map((e) => (
                    <SelectItem key={e} value={e}>
                      {e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-entitaid">ID entity</Label>
              <Input
                id="audit-entitaid"
                value={draft.entitaId}
                onChange={(e) => setDraft((d) => ({ ...d, entitaId: e.target.value }))}
                placeholder="UUID"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-userid">Uživatel (ID)</Label>
              <Input
                id="audit-userid"
                value={draft.userId}
                onChange={(e) => setDraft((d) => ({ ...d, userId: e.target.value }))}
                placeholder="UUID uživatele"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-pagesize">Velikost stránky</Label>
              <Input
                id="audit-pagesize"
                type="number"
                min={1}
                max={500}
                value={draft.pageSize}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    pageSize: Math.max(1, Math.min(500, Number(e.target.value) || 50)),
                  }))
                }
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleApply}>Použít filtry</Button>
            <Button variant="outline" onClick={handleReset}>
              Vyčistit
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Záznamy</CardTitle>
            {data && (
              <div className="text-sm text-muted-foreground">
                Celkem: <strong>{data.total}</strong> · Stránka {data.page} / {totalPages}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : listQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Načtení selhalo: {(listQuery.error as Error).message}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="w-48">Kdy</TableHead>
                    <TableHead className="w-32">Entita</TableHead>
                    <TableHead>EntitaId</TableHead>
                    <TableHead className="w-32">Akce</TableHead>
                    <TableHead>Kdo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.items ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        Žádné záznamy.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (data?.items ?? []).map((h) => {
                      const isOpen = expanded.has(h.id);
                      return (
                        <Fragment key={h.id}>
                          <TableRow>
                            <TableCell>
                              <button
                                type="button"
                                onClick={() => toggleExpand(h.id)}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {formatDateTime(h.kdy)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{h.entita}</Badge>
                            </TableCell>
                            <TableCell>
                              <EntityLink entita={h.entita} entitaId={h.entitaId} />
                            </TableCell>
                            <TableCell>
                              <AkceBadge akce={h.akce} />
                            </TableCell>
                            <TableCell className="text-sm">{h.kdoJmeno ?? '—'}</TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow>
                              <TableCell colSpan={6} className="bg-muted/20">
                                <DiffView pred={h.pred} po={h.po} komentar={h.komentar} />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              </div>

              {data && data.total > 0 && (
                <div className="mt-4 flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePage(-1)}
                    disabled={applied.page <= 1}
                  >
                    Předchozí
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {applied.page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePage(1)}
                    disabled={applied.page >= totalPages}
                  >
                    Další
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatDateTime(iso: string): string {
  // Bezpecnostni try-catch — kdyby backend poslal divnost, nezhroutime se.
  try {
    return new Date(iso).toLocaleString('cs-CZ');
  } catch {
    return iso;
  }
}

function AkceBadge({ akce }: { akce: string }) {
  const variant =
    akce === 'Vytvoreno'
      ? 'success'
      : akce === 'Smazano'
        ? 'destructive'
        : akce === 'Schvaleni'
          ? 'default'
          : 'secondary';
  return (
    <Badge variant={variant} className="font-mono text-[10px]">
      {akce}
    </Badge>
  );
}

function EntityLink({ entita, entitaId }: { entita: string; entitaId: string }) {
  // U entit, kde mame routu, vykreslime link. U ostatnich jen mono text.
  if (entita === 'Operativa') {
    return (
      <Link to={`/operativy/${entitaId}`} className="font-mono text-xs text-primary hover:underline">
        {entitaId}
      </Link>
    );
  }
  if (entita === 'Mechanizace') {
    // Detail mechanizace jako stranku nemame, ale aspon vedeme na seznam.
    return (
      <Link to="/mechanizace" className="font-mono text-xs text-primary hover:underline">
        {entitaId}
      </Link>
    );
  }
  return <span className="font-mono text-xs">{entitaId}</span>;
}

interface DiffViewProps {
  pred: string | null;
  po: string | null;
  komentar: string | null;
}

export function DiffView({ pred, po, komentar }: DiffViewProps) {
  return (
    <div className="space-y-3 py-2">
      {komentar && (
        <div className="text-sm">
          <span className="font-medium">Komentář:</span>{' '}
          <span className="text-muted-foreground">{komentar}</span>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Před</div>
          <pre className="max-h-72 overflow-auto rounded-md border bg-background p-3 text-xs">
            {pretty(pred) || <span className="text-muted-foreground">—</span>}
          </pre>
        </div>
        <div>
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Po</div>
          <pre className="max-h-72 overflow-auto rounded-md border bg-background p-3 text-xs">
            {pretty(po) || <span className="text-muted-foreground">—</span>}
          </pre>
        </div>
      </div>
    </div>
  );
}
